import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  FolderKanban,
  FolderPlus,
  LayoutDashboard,
  ListTodo,
  LogOut,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  TimerReset,
  TriangleAlert,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ApiRequestError,
  approvePlanning,
  completeTaskTimer,
  failTaskTimer,
  getAllProjects,
  getAllTasks,
  getCurrentUser,
  getHealth,
  getPlanning,
  getProjects,
  getTasks,
  getTaskTime,
  logout,
  pauseTaskTimer,
  reopenPlanning,
  startTaskTimer,
} from "./api";

import {
  APPLICATION_TIME_ZONE_LABEL,
  formatCurrentDate,
  formatTime,
  getWorkdayProgress,
} from "./timezone";

import type {
  AuthenticatedUser,
  HealthResponse,
  PlanningApprovalItem,
  PlanningData,
  Project,
  Task,
  TaskPriority,
  TaskStatus,
  TaskTimeSnapshot,
} from "./types";

import LoginScreen from "./LoginScreen";

import {
  CreateProjectModal,
  CreateTaskModal,
} from "./CreationModals";

import TaskActionButtons, {
  type TaskAction,
} from "./TaskActionButtons";

import TaskDetailsModal from "./TaskDetailsModal";

import AllTasksPanel from "./AllTasksPanel";

import ProjectsPanel from "./ProjectsPanel";

import PlanningPanel from "./PlanningPanel";

interface StatusDefinition {
  label: string;
  className: string;
}

const STATUS_DEFINITIONS: Record<
  TaskStatus,
  StatusDefinition
> = {
  pending: {
    label: "Ожидает",
    className: "status-pending",
  },

  in_progress: {
    label: "В работе",
    className: "status-progress",
  },

  waiting: {
    label: "На паузе",
    className: "status-waiting",
  },

  completed: {
    label: "Выполнена",
    className: "status-completed",
  },

  failed: {
    label: "Провалена",
    className: "status-failed",
  },
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  urgent: "Срочный",
};

function formatDurationFromSeconds(
  totalSeconds: number,
): string {
  const safeSeconds = Math.max(
    0,
    Math.floor(totalSeconds),
  );

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor(
    (safeSeconds % 3600) / 60,
  );

  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(seconds).padStart(2, "0"),
    ].join(":");
  }

  return [
    String(minutes).padStart(2, "0"),
    String(seconds).padStart(2, "0"),
  ].join(":");
}

function formatMinutes(
  minutes: number | null,
): string {
  if (minutes === null) {
    return "Не указано";
  }

  if (minutes < 60) {
    return `${minutes} мин`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} ч`;
  }

  return `${hours} ч ${remainingMinutes} мин`;
}

function StatCard({
  icon,
  label,
  value,
  helper,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <article className="stat-card">
      <div className="stat-icon">
        {icon}
      </div>

      <div className="stat-content">
        <span className="stat-label">
          {label}
        </span>

        <strong className="stat-value">
          {value}
        </strong>

        <span className="stat-helper">
          {helper}
        </span>
      </div>
    </article>
  );
}

function ProjectMarker({
  project,
}: {
  project: Task["project"];
}) {
  const color = project?.color ?? "#CBD5E1";

  return (
    <span
      className="project-marker"
      style={{
        backgroundColor: color,
      }}
    />
  );
}

function TaskCard({
  task,
  busyAction,
  onAction,
  onOpen,
}: {
  task: Task;
  busyAction: TaskAction | null;

  onAction: (
    task: Task,
    action: TaskAction,
  ) => Promise<void>;

  onOpen: (task: Task) => void;
}) {
  const status = STATUS_DEFINITIONS[task.status];

  return (
    <article
      className={[
        "task-card",
        task.status === "in_progress"
          ? "task-card-active"
          : "",
      ].join(" ")}
    >
      <div className="task-time">
        <strong>
          {formatTime(task.plannedStartAt)}
        </strong>

        <span>
          {formatMinutes(
            task.plannedDurationMinutes,
          )}
        </span>
      </div>

      <div className="task-main">
        <div className="task-heading">
          <div className="task-project">
            <ProjectMarker project={task.project} />

            <span>
              {task.project?.name ?? "Без проекта"}
            </span>
          </div>

          <span
            className={[
              "status-badge",
              status.className,
            ].join(" ")}
          >
            {status.label}
          </span>
        </div>

        <h3>{task.title}</h3>

        {task.description && (
          <p>{task.description}</p>
        )}

        <div className="task-meta">
          <span>
            Приоритет:{" "}
            {PRIORITY_LABELS[task.priority]}
          </span>

          {task.dueAt && (
            <span
              className={
                task.isOverdue
                  ? "meta-overdue"
                  : ""
              }
            >
              Срок: {formatTime(task.dueAt)}
            </span>
          )}

          <span>
            Факт:{" "}
            {formatDurationFromSeconds(
              task.actualDurationSeconds,
            )}
          </span>
        </div>

        <div className="task-actions-row">
          <TaskActionButtons
            task={task}
            busyAction={busyAction}
            onAction={(action) => {
              void onAction(task, action);
            }}
          />
        </div>
      </div>

      <button
        type="button"
        className="task-open-button"
        onClick={() => {
          onOpen(task);
        }}
        aria-label={`Открыть задачу ${task.title}`}
        title="Открыть подробности"
      >
        <ChevronRight size={20} />
      </button>
    </article>
  );
}

function EmptyTasks() {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <Sparkles size={28} />
      </div>

      <h3>Рабочий день пока свободен</h3>

      <p>
        Здесь появятся задачи, которые ты добавишь
        в план на сегодня.
      </p>
    </div>
  );
}

function Dashboard({
  user,
  onLogout,
}: {
  user: AuthenticatedUser;
  onLogout: () => Promise<void>;
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] =
    useState<Project[]>([]);

  const [
    isProjectModalOpen,
    setIsProjectModalOpen,
  ] = useState(false);

  const [
    isTaskModalOpen,
    setIsTaskModalOpen,
  ] = useState(false);

  const [
    taskActionInProgress,
    setTaskActionInProgress,
  ] = useState<{
    taskId: number;
    action: TaskAction;
  } | null>(null);

  const [
    selectedTask,
    setSelectedTask,
  ] = useState<Task | null>(null);

  const [
    isAllTasksOpen,
    setIsAllTasksOpen,
  ] = useState(false);

  const [
    allTasks,
    setAllTasks,
  ] = useState<Task[]>([]);

  const [
    isAllTasksLoading,
    setIsAllTasksLoading,
  ] = useState(false);

  const [
    allTasksError,
    setAllTasksError,
  ] = useState<string | null>(null);

  const [
    taskSearchQuery,
    setTaskSearchQuery,
  ] = useState("");

  const [
    isProjectsOpen,
    setIsProjectsOpen,
  ] = useState(false);

  const [
    allProjects,
    setAllProjects,
  ] = useState<Project[]>([]);

  const [
    isProjectsLoading,
    setIsProjectsLoading,
  ] = useState(false);

  const [
    projectsWorkspaceError,
    setProjectsWorkspaceError,
  ] = useState<string | null>(null);

  const [
    isPlanningOpen,
    setIsPlanningOpen,
  ] = useState(false);

  const [
    selectedPlanningDate,
    setSelectedPlanningDate,
  ] = useState(() =>
    new Intl.DateTimeFormat(
      "en-CA",
      {
        timeZone: "Europe/Samara",

        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      },
    ).format(new Date()),
  );

  const [
    planningData,
    setPlanningData,
  ] = useState<PlanningData | null>(null);

  const [
    isPlanningLoading,
    setIsPlanningLoading,
  ] = useState(false);

  const [
    isPlanningSubmitting,
    setIsPlanningSubmitting,
  ] = useState(false);

  const [
    planningError,
    setPlanningError,
  ] = useState<string | null>(null);

  const [health, setHealth] =
    useState<HealthResponse | null>(null);

  const [focus, setFocus] =
    useState<TaskTimeSnapshot | null>(null);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isRefreshing, setIsRefreshing] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [clock, setClock] =
    useState(() => Date.now());

  const loadData = useCallback(
    async (showLoader: boolean) => {
      if (showLoader) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const [
          tasksResponse,
          projectsResponse,
          healthResponse,
        ] = await Promise.all([
          getTasks(),
          getProjects(),
          getHealth(),
        ]);

        setTasks(tasksResponse.data);
        setProjects(projectsResponse.data);
        setHealth(healthResponse);
        setError(null);

        const activeTask =
          tasksResponse.data.find(
            (task) =>
              task.status === "in_progress",
          );

        if (activeTask) {
          const timeResponse =
            await getTaskTime(activeTask.id);

          setFocus(timeResponse.data);
        } else {
          setFocus(null);
        }
      } catch (requestError: unknown) {
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Не удалось загрузить данные.",
        );
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadData(true);
  }, [loadData]);

  useEffect(() => {
    const refreshTimer = window.setInterval(
      () => {
        void loadData(false);
      },
      15_000,
    );

    return () => {
      window.clearInterval(refreshTimer);
    };
  }, [loadData]);

  useEffect(() => {
    const clockTimer = window.setInterval(
      () => {
        setClock(Date.now());
      },
      1_000,
    );

    return () => {
      window.clearInterval(clockTimer);
    };
  }, []);

  const loadPlanning =
    useCallback(
      async (
        date: string =
          selectedPlanningDate,
      ): Promise<void> => {
        setIsPlanningLoading(true);

        try {
          const response =
            await getPlanning(date);

          setPlanningData(response.data);
          setPlanningError(null);
        } catch (requestError: unknown) {
          if (
            requestError
              instanceof ApiRequestError
            && requestError.status === 401
          ) {
            await onLogout();
            return;
          }

          setPlanningError(
            requestError instanceof Error
              ? requestError.message
              : "Не удалось загрузить план дня.",
          );
        } finally {
          setIsPlanningLoading(false);
        }
      },
      [
        onLogout,
        selectedPlanningDate,
      ],
    );

  const openPlanning =
    useCallback((): void => {
      setIsPlanningOpen(true);
      void loadPlanning();
    }, [loadPlanning]);

  const handlePlanningDateChange =
    useCallback(
      (date: string): void => {
        if (
          !/^\d{4}-\d{2}-\d{2}$/.test(
            date,
          )
        ) {
          return;
        }

        setSelectedPlanningDate(date);
        void loadPlanning(date);
      },
      [loadPlanning],
    );

  const handlePlanningApproval =
    useCallback(
      async (
        items: PlanningApprovalItem[],
      ): Promise<void> => {
        if (isPlanningSubmitting) {
          return;
        }

        setIsPlanningSubmitting(true);
        setPlanningError(null);

        try {
          const response =
            await approvePlanning(
              selectedPlanningDate,
              {
                items,
              },
            );

          setPlanningData(response.data);

          await loadData(false);
        } catch (requestError: unknown) {
          if (
            requestError
              instanceof ApiRequestError
            && requestError.status === 401
          ) {
            await onLogout();
            return;
          }

          setPlanningError(
            requestError instanceof Error
              ? requestError.message
              : "Не удалось подтвердить план.",
          );
        } finally {
          setIsPlanningSubmitting(false);
        }
      },
      [
        isPlanningSubmitting,
        loadData,
        onLogout,
        selectedPlanningDate,
      ],
    );

  const handlePlanningReopen =
    useCallback(
      async (): Promise<void> => {
        if (isPlanningSubmitting) {
          return;
        }

        setIsPlanningSubmitting(true);
        setPlanningError(null);

        try {
          const response =
            await reopenPlanning(
              selectedPlanningDate,
            );

          setPlanningData(response.data);
        } catch (requestError: unknown) {
          if (
            requestError
              instanceof ApiRequestError
            && requestError.status === 401
          ) {
            await onLogout();
            return;
          }

          setPlanningError(
            requestError instanceof Error
              ? requestError.message
              : "Не удалось вернуть план в черновик.",
          );
        } finally {
          setIsPlanningSubmitting(false);
        }
      },
      [
        isPlanningSubmitting,
        onLogout,
        selectedPlanningDate,
      ],
    );

  const loadAllProjects =
    useCallback(async (): Promise<void> => {
      setIsProjectsLoading(true);

      try {
        const response =
          await getAllProjects();

        setAllProjects(response.data);
        setProjectsWorkspaceError(null);
      } catch (requestError: unknown) {
        if (
          requestError instanceof ApiRequestError
          && requestError.status === 401
        ) {
          await onLogout();
          return;
        }

        setProjectsWorkspaceError(
          requestError instanceof Error
            ? requestError.message
            : "Не удалось загрузить проекты.",
        );
      } finally {
        setIsProjectsLoading(false);
      }
    }, [onLogout]);

  const openProjects =
    useCallback((): void => {
      setIsProjectsOpen(true);
      void loadAllProjects();
    }, [loadAllProjects]);

  const loadAllTasks =
    useCallback(async (): Promise<void> => {
      setIsAllTasksLoading(true);

      try {
        const response =
          await getAllTasks();

        setAllTasks(response.data);
        setAllTasksError(null);
      } catch (requestError: unknown) {
        if (
          requestError instanceof ApiRequestError
          && requestError.status === 401
        ) {
          await onLogout();
          return;
        }

        setAllTasksError(
          requestError instanceof Error
            ? requestError.message
            : "Не удалось загрузить задачи.",
        );
      } finally {
        setIsAllTasksLoading(false);
      }
    }, [onLogout]);

  const openAllTasks =
    useCallback((): void => {
      setIsAllTasksOpen(true);
      void loadAllTasks();
    }, [loadAllTasks]);

  const handleTaskAction =
    useCallback(
      async (
        task: Task,
        action: TaskAction,
      ): Promise<void> => {
        if (taskActionInProgress !== null) {
          return;
        }

        setTaskActionInProgress({
          taskId: task.id,
          action,
        });

        setError(null);

        try {
          switch (action) {
            case "start":
              await startTaskTimer(task.id);
              break;

            case "pause":
              await pauseTaskTimer(task.id);
              break;

            case "complete":
              await completeTaskTimer(task.id);
              break;

            case "fail":
              await failTaskTimer(task.id);
              break;
          }

          await loadData(false);

          if (isAllTasksOpen) {
            await loadAllTasks();
          }
        } catch (requestError: unknown) {
          if (
            requestError instanceof ApiRequestError
            && requestError.status === 401
          ) {
            await onLogout();
            return;
          }

          setError(
            requestError instanceof Error
              ? requestError.message
              : "Не удалось изменить состояние задачи.",
          );
        } finally {
          setTaskActionInProgress(null);
        }
      },
      [
        isAllTasksOpen,
        loadAllTasks,
        loadData,
        onLogout,
        taskActionInProgress,
      ],
    );

  const handleProjectCreated =
    useCallback(async () => {
      setIsProjectModalOpen(false);
      await loadData(false);
    }, [loadData]);

  const handleTaskCreated =
    useCallback(async () => {
      setIsTaskModalOpen(false);
      await loadData(false);
    }, [loadData]);

  const plannedMinutes = useMemo(
    () =>
      tasks.reduce(
        (total, task) =>
          total
          + (task.plannedDurationMinutes ?? 0),
        0,
      ),
    [tasks],
  );

  const actualSeconds = useMemo(
    () =>
      tasks.reduce(
        (total, task) =>
          total + task.actualDurationSeconds,
        0,
      ),
    [tasks],
  );

  const overdueCount = useMemo(
    () =>
      tasks.filter(
        (task) => task.isOverdue,
      ).length,
    [tasks],
  );

  const activeTask = useMemo(
    () =>
      tasks.find(
        (task) =>
          task.status === "in_progress",
      ) ?? null,
    [tasks],
  );

  const focusActualSeconds = useMemo(() => {
    if (!focus) {
      return 0;
    }

    let result =
      focus.fact.actualDurationSeconds;

    if (
      focus.timer.isRunning
      && focus.timer.activeStartedAt
    ) {
      result += Math.max(
        0,
        Math.floor(
          (
            clock
            - new Date(
                focus.timer.activeStartedAt,
              ).getTime()
          ) / 1000,
        ),
      );
    }

    return result;
  }, [clock, focus]);

  const workdayProgress =
    getWorkdayProgress();

  const userInitials = user.displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    || user.username.slice(0, 2).toUpperCase();

  return (
    <div className="application-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            P
          </div>

          <div>
            <strong>Planer</strong>
            <span>Рабочий день</span>
          </div>
        </div>

        <nav className="navigation">
          <button
            type="button"
            className="navigation-item navigation-item-active"
          >
            <LayoutDashboard size={20} />
            <span>Сегодня</span>
          </button>

          <button
            type="button"
            className="navigation-item"
            onClick={openAllTasks}
          >
            <ListTodo size={20} />
            <span>Все задачи</span>
          </button>

          <button
            type="button"
            className="navigation-item"
            onClick={openPlanning}
          >
            <CalendarDays size={20} />
            <span>Планирование</span>
          </button>

          <button
            type="button"
            className="navigation-item"
            onClick={openProjects}
          >
            <FolderKanban size={20} />
            <span>Проекты</span>

            <span className="navigation-counter">
              {projects.length}
            </span>
          </button>
        </nav>

        <div className="sidebar-projects">
          <div className="sidebar-section-title">
            <span>Проекты</span>

            <button
              type="button"
              className="sidebar-add-project"
              onClick={() => {
                setIsProjectModalOpen(true);
              }}
              aria-label="Создать проект"
              title="Создать проект"
            >
              <FolderPlus size={15} />
            </button>
          </div>

          {projects.length === 0 ? (
            <p className="sidebar-empty">
              Проекты пока не созданы
            </p>
          ) : (
            projects.slice(0, 5).map(
              (project) => (
                <div
                  key={project.id}
                  className="sidebar-project"
                >
                  <span
                    className="project-marker"
                    style={{
                      backgroundColor:
                        project.color
                        ?? "#CBD5E1",
                    }}
                  />

                  <span>{project.name}</span>
                </div>
              ),
            )
          )}
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className="navigation-item"
          >
            <Settings2 size={20} />
            <span>Настройки</span>
          </button>

          <div className="system-status">
            <span
              className={[
                "system-status-dot",
                health?.status === "ok"
                  ? "system-status-online"
                  : "",
              ].join(" ")}
            />

            <span>
              {health?.status === "ok"
                ? "Система работает"
                : "Проверка системы"}
            </span>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <p className="eyebrow">
              Мой рабочий день
            </p>

            <h1>{formatCurrentDate()}</h1>
          </div>

          <div className="topbar-actions">
            <label className="search-field">
              <Search size={18} />

              <input
                type="search"
                value={taskSearchQuery}
                onChange={(event) => {
                  setTaskSearchQuery(
                    event.target.value,
                  );
                }}
                onFocus={openAllTasks}
                placeholder="Найти задачу"
                aria-label="Найти задачу"
              />
            </label>

            <button
              type="button"
              className="refresh-button"
              onClick={() => {
                void loadData(false);
              }}
              disabled={isRefreshing}
            >
              <RefreshCw
                size={19}
                className={
                  isRefreshing
                    ? "rotating"
                    : ""
                }
              />

              <span>Обновить</span>
            </button>

            <button
              type="button"
              className="secondary-action-button"
              onClick={() => {
                setIsProjectModalOpen(true);
              }}
            >
              <FolderPlus size={18} />
              <span>Новый проект</span>
            </button>

            <button
              type="button"
              className="primary-button"
              onClick={() => {
                setIsTaskModalOpen(true);
              }}
            >
              <span>+</span>
              Новая задача
            </button>

            <button
              type="button"
              className="profile-button"
              onClick={() => {
                void onLogout();
              }}
              title="Выйти из Planer"
            >
              <span className="profile-avatar">
                {userInitials}
              </span>

              <span className="profile-copy">
                <strong>{user.displayName}</strong>
                <small>@{user.username}</small>
              </span>

              <LogOut size={17} />
            </button>
          </div>
        </header>

        {error && (
          <div className="error-banner">
            <TriangleAlert size={20} />

            <span>{error}</span>

            <button
              type="button"
              onClick={() => {
                void loadData(false);
              }}
            >
              Повторить
            </button>
          </div>
        )}

        <section className="workday-card">
          <div className="workday-header">
            <div className="workday-title-group">
              <div>
                <span className="workday-label">
                  Рабочий день
                </span>

                <strong>09:00 — 20:00</strong>
              </div>

              <span className="timezone-badge">
                {APPLICATION_TIME_ZONE_LABEL}
              </span>
            </div>

            <span className="workday-percent">
              {workdayProgress}%
            </span>
          </div>

          <div className="workday-track">
            <div
              className="workday-progress"
              style={{
                width: `${workdayProgress}%`,
              }}
            />
          </div>

          <div className="workday-scale">
            <span>09:00</span>
            <span>12:00</span>
            <span>15:00</span>
            <span>18:00</span>
            <span>20:00</span>
          </div>
        </section>

        <section className="stats-grid">
          <StatCard
            icon={<ListTodo size={22} />}
            label="Задачи"
            value={String(tasks.length)}
            helper="В активном списке"
          />

          <StatCard
            icon={<Clock3 size={22} />}
            label="План"
            value={formatMinutes(plannedMinutes)}
            helper="Запланировано"
          />

          <StatCard
            icon={<TimerReset size={22} />}
            label="Факт"
            value={
              formatDurationFromSeconds(
                actualSeconds,
              )
            }
            helper="Затрачено сегодня"
          />

          <StatCard
            icon={<TriangleAlert size={22} />}
            label="Просрочено"
            value={String(overdueCount)}
            helper={
              overdueCount === 0
                ? "Всё под контролем"
                : "Нужно внимание"
            }
          />
        </section>

        <div className="dashboard-grid">
          <section className="tasks-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">
                  Расписание
                </p>

                <h2>Задачи дня</h2>
              </div>

              <span className="panel-count">
                {tasks.length}
              </span>
            </div>

            <div className="tasks-list">
              {isLoading ? (
                <div className="loading-state">
                  <RefreshCw
                    size={24}
                    className="rotating"
                  />

                  <span>Загружаем задачи…</span>
                </div>
              ) : tasks.length === 0 ? (
                <EmptyTasks />
              ) : (
                tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    busyAction={
                      taskActionInProgress
                        ?.taskId === task.id
                        ? taskActionInProgress
                            .action
                        : null
                    }
                    onAction={
                      handleTaskAction
                    }
                    onOpen={(openedTask) => {
                      setSelectedTask(
                        openedTask,
                      );
                    }}
                  />
                ))
              )}
            </div>
          </section>

          <aside className="focus-column">
            <section className="focus-card">
              <div className="focus-card-heading">
                <div className="focus-icon">
                  <CircleDot size={22} />
                </div>

                <div>
                  <span>Фокус сейчас</span>
                  <strong>
                    {activeTask
                      ? "Задача в работе"
                      : "Нет активной задачи"}
                  </strong>
                </div>
              </div>

              {activeTask ? (
                <>
                  <div className="focus-project">
                    <ProjectMarker
                      project={activeTask.project}
                    />

                    <span>
                      {activeTask.project?.name
                        ?? "Без проекта"}
                    </span>
                  </div>

                  <h3>{activeTask.title}</h3>

                  <div className="focus-timer">
                    {formatDurationFromSeconds(
                      focusActualSeconds,
                    )}
                  </div>

                  <div className="focus-plan">
                    <span>
                      План:{" "}
                      {formatMinutes(
                        activeTask
                          .plannedDurationMinutes,
                      )}
                    </span>

                    <span>
                      {focus?.comparison
                        .completionPercent
                        ?? 0}
                      %
                    </span>
                  </div>

                  <div className="focus-progress-track">
                    <div
                      className="focus-progress"
                      style={{
                        width: `${Math.min(
                          100,
                          focus?.comparison
                            .completionPercent
                            ?? 0,
                        )}%`,
                      }}
                    />
                  </div>

                  <div className="focus-actions">
                    <TaskActionButtons
                      task={activeTask}
                      busyAction={
                        taskActionInProgress
                          ?.taskId
                          === activeTask.id
                          ? taskActionInProgress
                              .action
                          : null
                      }
                      onAction={(action) => {
                        void handleTaskAction(
                          activeTask,
                          action,
                        );
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="focus-empty">
                  <CheckCircle2 size={32} />

                  <p>
                    Запусти таймер задачи, и она
                    появится в этом блоке.
                  </p>
                </div>
              )}
            </section>

            <section className="recommendation-card">
              <div className="recommendation-icon">
                <Sparkles size={22} />
              </div>

              <div>
                <span>Помощник планирования</span>

                <strong>
                  Предложение плана появится здесь
                </strong>

                <p>
                  Позже приложение сможет
                  распределять задачи по свободному
                  времени рабочего дня.
                </p>
              </div>
            </section>

            <section className="summary-card">
              <div className="summary-row">
                <span>Активная задача</span>

                <strong>
                  {activeTask ? "1" : "0"}
                </strong>
              </div>

              <div className="summary-row">
                <span>Проектов</span>

                <strong>{projects.length}</strong>
              </div>

              <div className="summary-row">
                <span>Версия API</span>

                <strong>
                  {health?.version ?? "—"}
                </strong>
              </div>
            </section>
          </aside>
        </div>
      </main>

      {isProjectModalOpen && (
        <CreateProjectModal
          onClose={() => {
            setIsProjectModalOpen(false);
          }}
          onCreated={() => {
            void handleProjectCreated();
          }}
        />
      )}

      {isTaskModalOpen && (
        <CreateTaskModal
          projects={projects}
          onClose={() => {
            setIsTaskModalOpen(false);
          }}
          onCreated={() => {
            void handleTaskCreated();
          }}
        />
      )}

      {isPlanningOpen && (
        <PlanningPanel
          data={planningData}
          selectedDate={
            selectedPlanningDate
          }
          isLoading={
            isPlanningLoading
          }
          isSubmitting={
            isPlanningSubmitting
          }
          error={planningError}
          onDateChange={
            handlePlanningDateChange
          }
          onRefresh={() =>
            loadPlanning()
          }
          onApprove={
            handlePlanningApproval
          }
          onReopen={
            handlePlanningReopen
          }
          onClose={() => {
            setIsPlanningOpen(false);
          }}
        />
      )}

      {isProjectsOpen && (
        <ProjectsPanel
          projects={allProjects}
          isLoading={isProjectsLoading}
          error={projectsWorkspaceError}
          onRefresh={loadAllProjects}
          onCreateProject={() => {
            setIsProjectsOpen(false);
            setIsProjectModalOpen(true);
          }}
          onChanged={async () => {
            await loadData(false);
            await loadAllProjects();
          }}
          onSessionExpired={onLogout}
          onClose={() => {
            setIsProjectsOpen(false);
          }}
        />
      )}

      {isAllTasksOpen && (
        <AllTasksPanel
          tasks={allTasks}
          projects={projects}
          isLoading={isAllTasksLoading}
          error={allTasksError}
          searchQuery={taskSearchQuery}
          taskActionInProgress={
            taskActionInProgress
          }
          onSearchChange={
            setTaskSearchQuery
          }
          onRefresh={loadAllTasks}
          onCreateTask={() => {
            setIsAllTasksOpen(false);
            setIsTaskModalOpen(true);
          }}
          onOpenTask={(task) => {
            setSelectedTask(task);
          }}
          onTaskAction={
            handleTaskAction
          }
          onClose={() => {
            setIsAllTasksOpen(false);
          }}
        />
      )}

      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          projects={projects}
          onClose={() => {
            setSelectedTask(null);
          }}
          onUpdated={async (
            updatedTask,
          ) => {
            setSelectedTask(
              updatedTask,
            );

            await loadData(false);

            if (isAllTasksOpen) {
              await loadAllTasks();
            }
          }}
        />
      )}
    </div>
  );
}

function App() {
  const [authStatus, setAuthStatus] =
    useState<
      "checking"
      | "authenticated"
      | "unauthenticated"
    >("checking");

  const [user, setUser] =
    useState<AuthenticatedUser | null>(null);

  const [authError, setAuthError] =
    useState<string | null>(null);

  const checkAuthentication =
    useCallback(async () => {
      try {
        const response =
          await getCurrentUser();

        setUser(response.data.user);
        setAuthStatus("authenticated");
        setAuthError(null);
      } catch (requestError: unknown) {
        setUser(null);
        setAuthStatus("unauthenticated");

        if (
          requestError instanceof ApiRequestError
          && requestError.status === 401
        ) {
          setAuthError(null);
        } else {
          setAuthError(
            requestError instanceof Error
              ? requestError.message
              : "Не удалось проверить авторизацию.",
          );
        }
      }
    }, []);

  useEffect(() => {
    void checkAuthentication();
  }, [checkAuthentication]);

  const handleLogout =
    useCallback(async (): Promise<void> => {
      try {
        await logout();
      } catch {
        // Local logout still proceeds.
      } finally {
        setUser(null);
        setAuthStatus("unauthenticated");
        setAuthError(null);
      }
    }, []);

  if (authStatus === "checking") {
    return (
      <main className="auth-loading-screen">
        <div className="auth-loading-logo">
          P
        </div>

        <RefreshCw
          size={22}
          className="rotating"
        />

        <span>
          Проверяем защищённую сессию…
        </span>
      </main>
    );
  }

  if (
    authStatus === "unauthenticated"
    || user === null
  ) {
    return (
      <LoginScreen
        initialError={authError}
        onAuthenticated={(
          authenticatedUser,
        ) => {
          setUser(authenticatedUser);
          setAuthStatus("authenticated");
          setAuthError(null);
        }}
      />
    );
  }

  return (
    <Dashboard
      user={user}
      onLogout={handleLogout}
    />
  );
}

export default App;
